import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as statsApi from '../../shared/services/stats/statsApi';

const initialState = {
  dashboard: null,
  dashboardLoading: false,
  dashboardLastFetched: null,

  trends: null,
  trendsLoading: false,
  trendsLastFetched: null,

  demographics: null,
  demographicsLoading: false,

  expertsPerformance: null,
  expertsLoading: false,

  finance: null,
  financeLoading: false,

  complaints: null,
  complaintsLoading: false,

  consultations: null,
  consultationsLoading: false,

  chats: null,
  chatsLoading: false,

  support: null,
  supportLoading: false,

  anxiety: null,
  anxietyLoading: false,

  promo: null,
  promoLoading: false,

  partners: null,
  partnersLoading: false,

  live: null,
  liveLoading: false,
  liveLastFetched: null,

  error: null,
};

// Dashboard stats
export const fetchDashboardStats = createAsyncThunk(
  'stats/fetchDashboard',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getDashboardStats(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Trends
export const fetchTrends = createAsyncThunk(
  'stats/fetchTrends',
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const data = await statsApi.getTrends(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// User demographics
export const fetchUserDemographics = createAsyncThunk(
  'stats/fetchUserDemographics',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getUserDemographics(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Expert performance
export const fetchExpertPerformance = createAsyncThunk(
  'stats/fetchExpertPerformance',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getExpertPerformance(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Finance detailed
export const fetchFinanceDetailed = createAsyncThunk(
  'stats/fetchFinanceDetailed',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getFinanceDetailed(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Complaints
export const fetchComplaintStats = createAsyncThunk(
  'stats/fetchComplaints',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getComplaintStats(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Consultations analytics
export const fetchConsultationAnalytics = createAsyncThunk(
  'stats/fetchConsultations',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getConsultationAnalytics(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Chat analytics
export const fetchChatAnalytics = createAsyncThunk(
  'stats/fetchChats',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getChatAnalytics(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Support stats
export const fetchSupportStats = createAsyncThunk(
  'stats/fetchSupport',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getSupportStats(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Anxiety stats
export const fetchAnxietyStats = createAsyncThunk(
  'stats/fetchAnxiety',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getAnxietyStats(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Promo stats
export const fetchPromoStats = createAsyncThunk(
  'stats/fetchPromo',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getPromoStats(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Partner stats
export const fetchPartnerStats = createAsyncThunk(
  'stats/fetchPartners',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getPartnerStats(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Live metrics
export const fetchLiveMetrics = createAsyncThunk(
  'stats/fetchLive',
  async (params, { rejectWithValue }) => {
    try {
      const data = await statsApi.getLiveMetrics(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    clearStatsError: (state) => {
      state.error = null;
    },
    clearStats: (state) => {
      state.dashboard = null;
      state.trends = null;
    },
    updateLiveMetric: (state, action) => {
      state.live = { ...state.live, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchDashboardStats.pending, (state) => {
        state.dashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = action.payload.stats || action.payload.dashboard || action.payload;
        state.dashboardLastFetched = Date.now();
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.error = action.payload;
      })

      // Trends
      .addCase(fetchTrends.pending, (state) => {
        state.trendsLoading = true;
        state.error = null;
      })
      .addCase(fetchTrends.fulfilled, (state, action) => {
        state.trendsLoading = false;
        state.trends = action.payload.trends || action.payload.data || action.payload;
        state.trendsLastFetched = Date.now();
      })
      .addCase(fetchTrends.rejected, (state, action) => {
        state.trendsLoading = false;
        state.error = action.payload;
      })

      // User Demographics
      .addCase(fetchUserDemographics.pending, (state) => {
        state.demographicsLoading = true;
      })
      .addCase(fetchUserDemographics.fulfilled, (state, action) => {
        state.demographicsLoading = false;
        state.demographics = action.payload.stats || action.payload;
      })
      .addCase(fetchUserDemographics.rejected, (state, action) => {
        state.demographicsLoading = false;
        state.error = action.payload;
      })

      // Expert Performance
      .addCase(fetchExpertPerformance.pending, (state) => {
        state.expertsLoading = true;
      })
      .addCase(fetchExpertPerformance.fulfilled, (state, action) => {
        state.expertsLoading = false;
        state.expertsPerformance = action.payload.stats || action.payload;
      })
      .addCase(fetchExpertPerformance.rejected, (state, action) => {
        state.expertsLoading = false;
        state.error = action.payload;
      })

      // Finance Detailed
      .addCase(fetchFinanceDetailed.pending, (state) => {
        state.financeLoading = true;
      })
      .addCase(fetchFinanceDetailed.fulfilled, (state, action) => {
        state.financeLoading = false;
        state.finance = action.payload.stats || action.payload;
      })
      .addCase(fetchFinanceDetailed.rejected, (state, action) => {
        state.financeLoading = false;
        state.error = action.payload;
      })

      // Complaints
      .addCase(fetchComplaintStats.pending, (state) => {
        state.complaintsLoading = true;
      })
      .addCase(fetchComplaintStats.fulfilled, (state, action) => {
        state.complaintsLoading = false;
        state.complaints = action.payload.stats || action.payload;
      })
      .addCase(fetchComplaintStats.rejected, (state, action) => {
        state.complaintsLoading = false;
        state.error = action.payload;
      })

      // Consultations
      .addCase(fetchConsultationAnalytics.pending, (state) => {
        state.consultationsLoading = true;
      })
      .addCase(fetchConsultationAnalytics.fulfilled, (state, action) => {
        state.consultationsLoading = false;
        state.consultations = action.payload.stats || action.payload;
      })
      .addCase(fetchConsultationAnalytics.rejected, (state, action) => {
        state.consultationsLoading = false;
        state.error = action.payload;
      })

      // Chats
      .addCase(fetchChatAnalytics.pending, (state) => {
        state.chatsLoading = true;
      })
      .addCase(fetchChatAnalytics.fulfilled, (state, action) => {
        state.chatsLoading = false;
        state.chats = action.payload.stats || action.payload;
      })
      .addCase(fetchChatAnalytics.rejected, (state, action) => {
        state.chatsLoading = false;
        state.error = action.payload;
      })

      // Support
      .addCase(fetchSupportStats.pending, (state) => {
        state.supportLoading = true;
      })
      .addCase(fetchSupportStats.fulfilled, (state, action) => {
        state.supportLoading = false;
        state.support = action.payload.stats || action.payload;
      })
      .addCase(fetchSupportStats.rejected, (state, action) => {
        state.supportLoading = false;
        state.error = action.payload;
      })

      // Anxiety
      .addCase(fetchAnxietyStats.pending, (state) => {
        state.anxietyLoading = true;
      })
      .addCase(fetchAnxietyStats.fulfilled, (state, action) => {
        state.anxietyLoading = false;
        state.anxiety = action.payload.stats || action.payload;
      })
      .addCase(fetchAnxietyStats.rejected, (state, action) => {
        state.anxietyLoading = false;
        state.error = action.payload;
      })

      // Promo
      .addCase(fetchPromoStats.pending, (state) => {
        state.promoLoading = true;
      })
      .addCase(fetchPromoStats.fulfilled, (state, action) => {
        state.promoLoading = false;
        state.promo = action.payload.stats || action.payload;
      })
      .addCase(fetchPromoStats.rejected, (state, action) => {
        state.promoLoading = false;
        state.error = action.payload;
      })

      // Partners
      .addCase(fetchPartnerStats.pending, (state) => {
        state.partnersLoading = true;
      })
      .addCase(fetchPartnerStats.fulfilled, (state, action) => {
        state.partnersLoading = false;
        state.partners = action.payload.stats || action.payload;
      })
      .addCase(fetchPartnerStats.rejected, (state, action) => {
        state.partnersLoading = false;
        state.error = action.payload;
      })

      // Live Metrics
      .addCase(fetchLiveMetrics.pending, (state) => {
        state.liveLoading = true;
      })
      .addCase(fetchLiveMetrics.fulfilled, (state, action) => {
        state.liveLoading = false;
        state.live = action.payload.metrics || action.payload.live || action.payload.data || action.payload;
        state.liveLastFetched = Date.now();
      })
      .addCase(fetchLiveMetrics.rejected, (state, action) => {
        state.liveLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearStatsError, clearStats, updateLiveMetric } = statsSlice.actions;
export default statsSlice.reducer;
